import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SpaRedirect from '@/components/seo/SpaRedirect';
import CityReviewsBlock from '@/components/common/CityReviewsBlock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServiceAreaPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        console.log(`Fetching service area page for slug: ${slug}`);
        
        // Authoritative single-page lookup by slug.
        const directRes = await fetch(`${API_URL}/api/service-areas/page-by-slug/${encodeURIComponent(slug)}`);

        if (directRes.ok) {
          const pageData = await directRes.json();
          setPage(pageData);
        } else if (directRes.status === 404) {
          // Genuine missing page -> allow the noindex 404 branch.
          setNotFound(true);
        } else {
          // Transient (5xx / rate limit). Do NOT noindex a real page.
          console.error('Transient error loading page:', directRes.status);
          setError('transient');
        }
      } catch (err) {
        // Network failure = transient; never noindex on this.
        console.error('Error loading service area page:', err);
        setError('transient');
      }
      setLoading(false);
    };
    fetchPage();
  }, [slug]);

  // Belt-and-suspenders: when the page loads successfully, synchronously
  // lock the SEO head signals (robots + canonical + hreflang) so any race
  // with <RouteRobotsManager> or Helmet's async hydration can't strip them
  // before Googlebot's render snapshot.
  useEffect(() => {
    if (!page) return undefined;

    // 1. Lock robots = index, follow
    document.head.querySelectorAll('meta[name="robots"]').forEach((el) => el.remove());
    const robotsMeta = document.createElement('meta');
    robotsMeta.setAttribute('name', 'robots');
    robotsMeta.setAttribute('content', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    robotsMeta.dataset.fwdLocked = '1';
    document.head.appendChild(robotsMeta);

    // 2. Lock canonical to the EN URL
    const enUrl = `https://fortworthdental.com/${page.full_slug}`;
    document.head.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());
    const canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', enUrl);
    canonicalLink.dataset.fwdLocked = '1';
    document.head.appendChild(canonicalLink);

    // 3. Lock hreflang alternates (EN + ES + x-default) if ES translation
    // exists on this page. Fixes "Alternate page with proper canonical tag"
    // failures in GSC by giving Google the bidirectional language pair.
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    const esSlug = (page.translation_es || {}).full_slug;
    const alts = [
      { hreflang: 'en', href: enUrl },
      { hreflang: 'en-US', href: enUrl },
      { hreflang: 'x-default', href: enUrl },
    ];
    if (esSlug) {
      const esUrl = `https://fortworthdental.com/es/${esSlug}`;
      alts.push({ hreflang: 'es', href: esUrl });
      alts.push({ hreflang: 'es-US', href: esUrl });
    }
    alts.forEach(({ hreflang, href }) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', hreflang);
      link.setAttribute('href', href);
      link.dataset.fwdLocked = '1';
      document.head.appendChild(link);
    });

    return () => {
      document.head.querySelectorAll('meta[name="robots"][data-fwd-locked="1"]').forEach((el) => el.remove());
      document.head.querySelectorAll('link[rel="canonical"][data-fwd-locked="1"]').forEach((el) => el.remove());
      document.head.querySelectorAll('link[rel="alternate"][hreflang][data-fwd-locked="1"]').forEach((el) => el.remove());
    };
  }, [page]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (notFound) {
    // DEFINITIVE 404 only (deleted/nonexistent location page) -> clean redirect
    // to the areas hub instead of a 200+noindex soft-404.
    return <SpaRedirect to="/areas-we-serve" message="Redirecting…" />;
  }

  if (!page) {
    // Transient fetch error: do NOT render NotFound (no noindex). Keep the page
    // in a neutral loading state so Googlebot retains the raw HTML's default
    // index,follow and simply retries later — never wrongly noindex a real page.
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.meta_description} />
        {/* robots / canonical / hreflang are set imperatively in the
            useEffect above with data-fwd-locked="1" so RouteRobotsManager
            and Helmet's async hydration cannot strip them before Google's
            render snapshot. */}
        <script type="application/ld+json">
          {JSON.stringify(page.schema_json)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center gap-2 text-teal-200 text-sm mb-4">
              <Link to="/" className="hover:text-white">Home</Link>
              <span>/</span>
              <Link to={`/${page.service_slug}`} className="hover:text-white">{page.service}</Link>
              <span>/</span>
              <span className="text-white">{page.city}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{page.h1}</h1>
            <p className="text-teal-100 text-lg">
              Just {page.distance_miles} minutes from {page.city} • 1,100+ 5-Star Reviews
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        </div>

        {/* City-tagged Google reviews — local trust + conversion */}
        <CityReviewsBlock city={page.city} />

        {/* Related Services */}
        <div className="bg-gray-50 py-12">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">More Services for {page.city} Residents</h2>
            <div className="grid md:grid-cols-4 gap-4">
              <Link to="/dental-implants" className="bg-white p-4 rounded-lg shadow hover:shadow-md transition">
                <h3 className="font-semibold text-teal-600">Dental Implants</h3>
                <p className="text-sm text-gray-600">Permanent tooth replacement</p>
              </Link>
              <Link to="/invisalign" className="bg-white p-4 rounded-lg shadow hover:shadow-md transition">
                <h3 className="font-semibold text-teal-600">Invisalign</h3>
                <p className="text-sm text-gray-600">Clear teeth aligners</p>
              </Link>
              <Link to="/lanap" className="bg-white p-4 rounded-lg shadow hover:shadow-md transition">
                <h3 className="font-semibold text-teal-600">LANAP Treatment</h3>
                <p className="text-sm text-gray-600">Laser gum therapy</p>
              </Link>
              <Link to="/first-fit-veneers" className="bg-white p-4 rounded-lg shadow hover:shadow-md transition border-2 border-teal-100">
                <h3 className="font-semibold text-teal-600">First Fit Veneers</h3>
                <p className="text-sm text-gray-600">Same-day custom veneers</p>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceAreaPage;
